import React, { useEffect, useState, useCallback } from "react";
import { FileText, Loader2, ArrowLeft, Plus, Trash2, Send, Copy, Eye, RefreshCw, Wrench, Sparkles, TrendingUp, Briefcase, Play, CheckCircle2, Star, ShieldAlert, Gauge, Check, X, Navigation } from "lucide-react";
import { api, adminHeaders, mediaUrl, waLink } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full rounded-lg bg-black/40 border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]";
const label = "text-xs uppercase tracking-wide text-muted-c";
const VIEWS = ["Needs Quote", "Draft", "Sent", "Viewed", "Accepted", "Declined", "Revised", "All"];
const SERVICES = ["General Cleaning", "Standard Move-In / Move-Out", "Deep Move-In / Move-Out", "Post-Construction", "Premium Property Detail"];
const money = (n, c = "\u20aa") => (n || n === 0) ? `${c}${Number(n).toLocaleString()}` : "\u2014";
const STATUS_CLS = {
  Draft: "bg-white/10 text-white/70 border-white/20", Sent: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  Viewed: "bg-amber-500/15 text-amber-300 border-amber-500/30", Accepted: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Declined: "bg-red-500/15 text-red-300 border-red-500/30", Revised: "bg-purple-500/15 text-purple-300 border-purple-500/30",
};
const CONF = {
  high: { label: "High confidence", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", dot: "#6ee7b7" },
  medium: { label: "Medium confidence", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30", dot: "#E7CE93" },
  low: { label: "Low confidence", cls: "bg-red-500/15 text-red-300 border-red-500/30", dot: "#f4a4a4" },
};

function ConfidenceBadge({ confidence, size = "sm" }) {
  if (!confidence || !confidence.level) return null;
  const c = CONF[confidence.level] || CONF.low;
  const pad = size === "sm" ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border ${c.cls} ${pad}`} data-testid="quote-confidence-badge" title={`${confidence.ok_count}/${confidence.total} readiness checks passed`}>
      <Gauge className="h-3 w-3" /> {c.label} · {confidence.ok_count}/{confidence.total}
    </span>
  );
}

function Field({ l, children }) { return <label className="block space-y-1"><span className={label}>{l}</span>{children}</label>; }
function Sel({ l, value, onChange, options }) {
  return <Field l={l}><select className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)}>{options.map((o) => <option key={o} value={o}>{o}</option>)}</select></Field>;
}

export default function AdminQuotes({ initialQuoteId = null, onConsumeInitial }) {
  const [view, setView] = useState("Needs Quote");
  const [data, setData] = useState({ quotes: [], needs_quote: [] });
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  // Open a specific quote when navigated here (e.g. from the Assessment Workbench).
  useEffect(() => {
    if (initialQuoteId) {
      setActiveId(initialQuoteId);
      onConsumeInitial && onConsumeInitial();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuoteId]);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/quotes?status=${encodeURIComponent(view)}`, { headers: adminHeaders() })
      .then(({ data }) => setData({ quotes: data.quotes || [], needs_quote: data.needs_quote || [] }))
      .catch(() => toast.error("Couldn't load quotes")).finally(() => setLoading(false));
  }, [view]);
  useEffect(() => { load(); }, [load]);

  const createFromLead = async (leadId) => {
    try {
      const { data } = await api.post(`/admin/quotes/from-lead/${leadId}`, {}, { headers: adminHeaders() });
      toast.success("Quote draft created"); setActiveId(data.id);
    } catch { toast.error("Could not create quote"); }
  };

  if (activeId) return <QuoteEditor id={activeId} onBack={() => { setActiveId(null); load(); }} />;

  return (
    <div data-testid="admin-quotes">
      <div className="flex items-center gap-2 mb-4"><FileText className="h-5 w-5 text-gold-soft" /><h3 className="font-display text-lg font-semibold text-white">Quotes</h3></div>
      <div className="flex gap-2 flex-wrap mb-5">
        {VIEWS.map((v) => (
          <button key={v} onClick={() => setView(v)} data-testid={`quotes-view-${v.replace(/\s/g, "-").toLowerCase()}`} className={`rounded-full px-3 py-1 text-xs border ${view === v ? "bg-gold text-black border-gold" : "border-[color:var(--border-subtle)] text-white/70 hover:bg-white/5"}`}>{v}</button>
        ))}
      </div>
      {loading ? <div className="ascendia-card p-6 flex items-center gap-2 text-muted-c"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div> : (
        view === "Needs Quote" ? (
          data.needs_quote.length === 0 ? <p className="text-muted-c text-sm">No leads waiting for a quote. </p> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.needs_quote.map((l) => (
                <div key={l.id} className="ascendia-card p-4" data-testid="needs-quote-card">
                  <div className="flex justify-between items-start"><span className="font-semibold text-white">{l.full_name}</span><span className="text-[10px] font-mono text-gold-soft">{l.ref}</span></div>
                  <p className="text-sm text-secondary mt-1">{l.service_required || "\u2014"}</p>
                  <p className="text-xs text-muted-c mt-1">{[l.city, l.size_sqm && `${l.size_sqm} sqm`].filter(Boolean).join(" \u00b7 ")}</p>
                  <p className="text-xs text-muted-c mt-1">{(l.media || []).length} media · {l.phone}</p>
                  <button onClick={() => createFromLead(l.id)} data-testid="create-quote-btn" className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-gold py-2 text-xs font-semibold text-black hover:bg-gold-soft"><Plus className="h-3.5 w-3.5" /> Create Quote</button>
                </div>
              ))}
            </div>
          )
        ) : (
          data.quotes.length === 0 ? <p className="text-muted-c text-sm">No quotes in this view.</p> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.quotes.map((q) => (
                <button key={q.id} onClick={() => setActiveId(q.id)} data-testid="quote-card" className="ascendia-card p-4 text-left hover:bg-white/[0.03]">
                  <div className="flex justify-between items-start">
                    <span className="font-semibold text-white">{(q.lead_snapshot || {}).full_name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_CLS[q.status] || "border-white/20 text-white/60"}`}>{q.status}</span>
                  </div>
                  <p className="text-xs font-mono text-gold-soft mt-1">{q.ref} {q.version > 1 ? `\u00b7 v${q.version}` : ""}</p>
                  <p className="text-sm text-secondary mt-1">{(q.assessment || {}).service || "\u2014"}</p>
                  <div className="flex items-center justify-between mt-2 gap-2">
                    <p className="text-xs text-muted-c truncate">{(q.packages || []).length} option(s) · {(q.lead_snapshot || {}).city || ""}</p>
                    <ConfidenceBadge confidence={q.confidence} />
                  </div>
                </button>
              ))}
            </div>
          )
        )
      )}
    </div>
  );
}

function QuotePropertyReview({ snap }) {
  return (
    <section className="ascendia-card p-4">
      <h4 className="font-display font-semibold text-white mb-3">Property & customer</h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 gap-x-4 text-sm">
        {[["Phone", snap.phone], ["Email", snap.email], ["Address", [snap.address, snap.city].filter(Boolean).join(", ")], ["Size", snap.size_sqm && `${snap.size_sqm} sqm`], ["Rooms", snap.rooms], ["Baths", snap.bathrooms], ["Furnishing", snap.furnishing], ["State", snap.property_state], ["Requested", snap.service_required], ["Desired date", snap.desired_date], ["Budget", snap.budget_range]].filter(([, v]) => v).map(([k, v]) => (
          <div key={k}><p className={label}>{k}</p><p className="text-white break-words">{v}</p></div>
        ))}
      </div>
      {snap.notes && <p className="mt-3 text-sm text-secondary border-l-2 border-gold/40 pl-2">{snap.notes}</p>}
      {(snap.media || []).length > 0 && (
        <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="quote-media">
          {snap.media.map((m, i) => (m.content_type || "").startsWith("video") ? (
            <video key={m.url || m.path || `qm-${i}`} src={mediaUrl(m.url)} controls className="rounded-lg border border-[color:var(--border-subtle)] aspect-square object-cover bg-black" />
          ) : (
            <a key={m.url || m.path || `qm-${i}`} href={mediaUrl(m.url)} target="_blank" rel="noopener noreferrer"><img src={mediaUrl(m.url)} alt="" className="rounded-lg border border-[color:var(--border-subtle)] aspect-square object-cover hover:opacity-80" /></a>
          ))}
        </div>
      )}
    </section>
  );
}

function QuoteSendInfo({ sendInfo, snap }) {
  return (
    <section className="ascendia-card p-4" data-testid="quote-send-info">
      <p className="text-sm text-white mb-2">Secure quote link</p>
      <div className="flex gap-2">
        <input readOnly className={inputCls} value={sendInfo.link} />
        <button onClick={() => { navigator.clipboard?.writeText(sendInfo.link); toast.success("Link copied"); }} className="flex-shrink-0 h-9 w-9 rounded-lg border border-[color:var(--border-subtle)] text-gold-soft"><Copy className="h-4 w-4" /></button>
        <a href={waLink(snap.phone, sendInfo.whatsapp_text)} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 rounded-lg border border-[color:var(--border-subtle)] px-3 flex items-center text-xs text-white">WhatsApp</a>
      </div>
    </section>
  );
}

function QuoteEditor({ id, onBack }) {
  const [q, setQ] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendInfo, setSendInfo] = useState(null);
  const [gateOpen, setGateOpen] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      api.get(`/admin/quotes/${id}`, { headers: adminHeaders() }),
      api.get(`/quote-config`),
    ]).then(([a, b]) => { setQ(a.data); setCfg(b.data); }).catch(() => toast.error("Couldn't load quote"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!q || !cfg) return <div className="ascendia-card p-6 flex items-center gap-2 text-muted-c"><Loader2 className="h-4 w-4 animate-spin" /> Loading quote…</div>;

  const snap = q.lead_snapshot || {};
  const a = q.assessment || {};
  const setA = (k, v) => setQ({ ...q, assessment: { ...q.assessment, [k]: v } });
  const setPI = (k, v) => setQ({ ...q, profit_inputs: { ...q.profit_inputs, [k]: v } });
  const readOnly = q.status === "Accepted";

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/quotes/${id}`, {
        assessment: q.assessment, style: q.style, packages: q.packages, profit_inputs: q.profit_inputs, terms: q.terms,
      }, { headers: adminHeaders() });
      setQ(data); toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const doSend = async () => {
    setGateOpen(false);
    await save();
    try {
      const { data } = await api.post(`/admin/quotes/${id}/send`, { origin: window.location.origin }, { headers: adminHeaders() });
      setSendInfo(data);
      if (data.email_result?.sent) toast.success("Quote sent by email");
      else if ((data.email_result?.reason || "").includes("403")) toast.info("Email not sent — verify SendGrid sender.");
      else toast.success("Quote link ready");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
  };
  // Confidence Gate — never let a shaky quote rush out the door.
  const send = async () => {
    if ((q.confidence?.level || "high") !== "high") { setGateOpen(true); return; }
    await doSend();
  };
  const revise = async () => { try { const { data } = await api.post(`/admin/quotes/${id}/revise`, {}, { headers: adminHeaders() }); setQ(data); toast.success("Revision started"); } catch { toast.error("Failed"); } };
  const autoDistance = async () => {
    try {
      const { data } = await api.post(`/admin/quotes/${id}/auto-distance`, {}, { headers: adminHeaders() });
      setQ(data);
      const m = data.distance_meta || {};
      toast.success(`Distance set: ${m.distance_text || (data.profit_inputs?.distance_km + " km")}${m.duration_text ? ` · ${m.duration_text} drive` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not fetch distance"); }
  };
  const convert = async () => { try { await api.post(`/admin/quotes/${id}/convert-to-job`, {}, { headers: adminHeaders() }); toast.success("Job created from accepted quote"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

  const updPkg = (i, k, v) => { const p = [...q.packages]; p[i] = { ...p[i], [k]: v }; setQ({ ...q, packages: p }); };
  const toggleArr = (i, key, item) => { const p = [...q.packages]; const arr = p[i][key] || []; p[i] = { ...p[i], [key]: arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item] }; setQ({ ...q, packages: p }); };
  const addPkg = () => setQ({ ...q, packages: [...q.packages, { id: Math.random().toString(36).slice(2, 8), name: "New option", description: "", price: "", deposit: "", discount: "", scope_included: [], exclusions: [], addons: [], date: "", notes: "", recommended: false }] });
  const rmPkg = (i) => setQ({ ...q, packages: q.packages.filter((_, x) => x !== i) });
  const setRecommended = (i) => setQ({ ...q, packages: q.packages.map((p, x) => ({ ...p, recommended: x === i })) });

  const sug = q.suggestion || {};
  const prof = q.profit || {};
  const conf = q.confidence || null;
  const tr = prof.travel || {};

  return (
    <div data-testid="quote-editor" className="space-y-5 max-w-3xl mx-auto">
      <div className="flex items-center justify-between gap-3">
        <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-c hover:text-white"><ArrowLeft className="h-4 w-4" /> Quotes</button>
        <div className="flex items-center gap-2">
          <ConfidenceBadge confidence={conf} size="md" />
          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_CLS[q.status] || "border-white/20"}`}>{q.status}{q.version > 1 ? ` \u00b7 v${q.version}` : ""}</span>
        </div>
      </div>
      <div><h3 className="font-display text-xl font-semibold text-white">{snap.full_name} <span className="text-sm font-mono text-gold-soft">{q.ref}</span></h3></div>

      {/* Lead review */}
      <QuotePropertyReview snap={snap} />

      {/* Private assessment */}
      <section className="ascendia-card p-4">
        <div className="flex items-center gap-2 mb-3"><Wrench className="h-4 w-4 text-gold-soft" /><h4 className="font-display font-semibold text-white">Private assessment</h4><span className="text-[10px] text-muted-c">(never shown to customer)</span></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Sel l="Service" value={a.service} onChange={(v) => setA("service", v)} options={["", ...SERVICES]} />
          <Sel l="Condition" value={a.condition} onChange={(v) => setA("condition", v)} options={cfg.conditions} />
          <Sel l="Construction level" value={a.construction} onChange={(v) => setA("construction", v)} options={cfg.construction_levels} />
          <Sel l="Access" value={a.access} onChange={(v) => setA("access", v)} options={cfg.access_levels} />
          <Sel l="Window complexity" value={a.window_complexity} onChange={(v) => setA("window_complexity", v)} options={cfg.complexity_levels} />
          <Sel l="Kitchen complexity" value={a.kitchen_complexity} onChange={(v) => setA("kitchen_complexity", v)} options={cfg.complexity_levels} />
          <Sel l="Bathroom complexity" value={a.bathroom_complexity} onChange={(v) => setA("bathroom_complexity", v)} options={cfg.complexity_levels} />
          <Field l="Est. labour hours"><input className={inputCls} value={a.estimated_hours || ""} onChange={(e) => setA("estimated_hours", e.target.value)} /></Field>
          <Field l="Workers"><input className={inputCls} value={a.workers || ""} onChange={(e) => setA("workers", e.target.value)} /></Field>
        </div>
        <Field l="Internal notes"><textarea rows={2} className={`${inputCls} mt-2`} value={a.internal_notes || ""} onChange={(e) => setA("internal_notes", e.target.value)} /></Field>
      </section>

      {/* Pricing assistance + profit */}
      <section className="ascendia-card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-gold-soft" /><h4 className="font-display font-semibold text-white">Pricing assistance</h4></div>
          <button onClick={save} className="inline-flex items-center gap-1 text-xs text-gold-soft hover:text-gold"><RefreshCw className="h-3.5 w-3.5" /> Recalculate</button>
        </div>
        {sug.available ? (
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg border border-[color:var(--border-subtle)] bg-black/30 py-2"><p className={label}>Range</p><p className="text-sm font-semibold text-white">{money(sug.low, sug.currency)}–{money(sug.high, sug.currency)}</p></div>
            <div className="rounded-lg border border-[color:var(--border-subtle)] bg-black/30 py-2"><p className={label}>Suggested</p><p className="text-sm font-semibold text-gold-soft">{money(sug.target, sug.currency)}</p></div>
            <div className="rounded-lg border border-[color:var(--border-subtle)] bg-black/30 py-2"><p className={label}>Labour hrs</p><p className="text-sm font-semibold text-white">{sug.labor_hours}</p></div>
          </div>
        ) : <p className="text-xs text-muted-c">{sug.reason || "Fill assessment then Recalculate for a suggestion."} You always set the final price.</p>}

        <div className="flex items-center gap-2 mt-4 mb-2"><TrendingUp className="h-4 w-4 text-gold-soft" /><h4 className="font-display font-semibold text-white text-sm">Profit preview (private)</h4></div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[["labor_cost", "Labour cost"], ["travel", "Travel (manual)"], ["supplies", "Supplies"], ["equipment_other", "Equip/other"]].map(([k, l]) => (
            <Field key={k} l={l}><input className={inputCls} value={q.profit_inputs?.[k] || ""} onChange={(e) => setPI(k, e.target.value)} /></Field>
          ))}
        </div>
        {/* Travel Estimator — distance auto-costs round trip so far jobs keep their margin */}
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
          <Field l="Distance (km, one way)">
            <div className="flex gap-1.5">
              <input className={inputCls} value={q.profit_inputs?.distance_km || ""} onChange={(e) => setPI("distance_km", e.target.value)} data-testid="quote-distance-km" placeholder="0" />
              <button type="button" onClick={autoDistance} title="Auto-fill from the property address (Google Maps)" data-testid="quote-auto-distance" className="flex-shrink-0 inline-flex items-center gap-1 rounded-lg border border-[color:var(--border-subtle)] px-2.5 text-xs text-gold-soft hover:bg-white/5"><Navigation className="h-3.5 w-3.5" /> Auto</button>
            </div>
          </Field>
          <Field l="Labour hours"><input className={inputCls} value={q.profit_inputs?.labor_hours || ""} onChange={(e) => setPI("labor_hours", e.target.value)} placeholder="0" /></Field>
          {tr.distance_cost > 0 && (
            <div className="sm:col-span-2 flex items-center gap-2 rounded-lg border border-[color:var(--border-subtle)] bg-black/30 px-3 py-2" data-testid="quote-travel-breakdown">
              <Navigation className="h-3.5 w-3.5 text-gold-soft shrink-0" />
              <p className="text-[11px] text-secondary leading-tight">{tr.round_trip_km} km round trip × {money(tr.rate_per_km)}/km = <span className="text-gold-soft font-semibold">{money(tr.distance_cost)}</span> travel</p>
            </div>
          )}
        </div>
        <div className="grid grid-cols-4 gap-2 mt-3 text-center">
          <div><p className={label}>Cost</p><p className="text-sm text-white">{money(prof.total_cost)}</p></div>
          <div><p className={label}>Profit</p><p className={`text-sm font-semibold ${(prof.profit ?? 0) >= 0 ? "text-emerald-300" : "text-red-300"}`}>{money(prof.profit)}</p></div>
          <div><p className={label}>Margin</p><p className="text-sm text-gold-soft">{prof.margin == null ? "\u2014" : `${prof.margin}%`}</p></div>
          <div><p className={label}>₪/hr</p><p className="text-sm text-white">{prof.revenue_per_labor_hour == null ? "\u2014" : money(prof.revenue_per_labor_hour)}</p></div>
        </div>
        {prof.guardrail && prof.guardrail.evaluated && !prof.guardrail.ok && (
          <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-amber-500/40 bg-amber-500/[0.08] px-3 py-2.5" data-testid="quote-guardrail-warning">
            <ShieldAlert className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-semibold text-amber-200">Below margin guardrail</p>
              <p className="text-[11px] text-amber-200/80 mt-0.5">{prof.guardrail.warning} Adjust price or costs before sending.</p>
            </div>
          </div>
        )}
        {prof.guardrail && prof.guardrail.evaluated && prof.guardrail.ok && (
          <p className="mt-3 flex items-center gap-1.5 text-[11px] text-emerald-300" data-testid="quote-guardrail-ok">
            <CheckCircle2 className="h-3.5 w-3.5" /> Margin meets the {prof.guardrail.min_margin}% guardrail.
          </p>
        )}
        {conf && (conf.factors || []).length > 0 && (
          <div className="mt-4 rounded-lg border border-[color:var(--border-subtle)] bg-black/20 p-3" data-testid="quote-confidence-panel">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-white flex items-center gap-1.5"><Gauge className="h-3.5 w-3.5 text-gold-soft" /> Quote readiness</p>
              <ConfidenceBadge confidence={conf} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1.5">
              {conf.factors.map((f) => (
                <div key={f.key} className="flex items-center gap-1.5 text-[11px]" data-testid={`conf-factor-${f.key}`}>
                  {f.ok ? <Check className="h-3.5 w-3.5 text-emerald-300 shrink-0" /> : <X className="h-3.5 w-3.5 text-red-300 shrink-0" />}
                  <span className={f.ok ? "text-secondary" : "text-muted-c"}>{f.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Quote builder */}
      <section className="ascendia-card p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display font-semibold text-white">Quote builder</h4>
          <div className="flex rounded-lg border border-[color:var(--border-subtle)] overflow-hidden text-xs">
            {["single", "packages"].map((st) => <button key={st} onClick={() => setQ({ ...q, style: st })} className={`px-3 py-1.5 ${q.style === st ? "bg-gold text-black" : "text-white/70"}`}>{st === "single" ? "Single quote" : "3 Packages"}</button>)}
          </div>
        </div>
        <div className="space-y-4">
          {q.packages.map((p, i) => (
            <div key={p.id} className="rounded-xl border border-[color:var(--border-subtle)] bg-black/20 p-3" data-testid="quote-package">
              <div className="flex items-center gap-2 mb-2">
                <input className={`${inputCls} font-semibold`} value={p.name} onChange={(e) => updPkg(i, "name", e.target.value)} placeholder="Option name" />
                <button onClick={() => setRecommended(i)} title="Mark recommended" className={`flex-shrink-0 h-9 w-9 rounded-lg border flex items-center justify-center ${p.recommended ? "bg-gold text-black border-gold" : "border-[color:var(--border-subtle)] text-white/50"}`}><Star className="h-4 w-4" /></button>
                {q.packages.length > 1 && <button onClick={() => rmPkg(i)} className="flex-shrink-0 h-9 w-9 rounded-lg border border-[color:var(--border-subtle)] text-red-300"><Trash2 className="h-4 w-4" /></button>}
              </div>
              <textarea rows={2} className={inputCls} value={p.description} onChange={(e) => updPkg(i, "description", e.target.value)} placeholder="Short description" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                <Field l="Price ₪"><input className={inputCls} value={p.price} onChange={(e) => updPkg(i, "price", e.target.value)} /></Field>
                <Field l="Deposit ₪"><input className={inputCls} value={p.deposit} onChange={(e) => updPkg(i, "deposit", e.target.value)} /></Field>
                <Field l="Discount ₪"><input className={inputCls} value={p.discount} onChange={(e) => updPkg(i, "discount", e.target.value)} /></Field>
                <Field l="Date"><input className={inputCls} value={p.date} onChange={(e) => updPkg(i, "date", e.target.value)} placeholder="e.g. Aug 20" /></Field>
              </div>
              {/* Scope builder */}
              <details className="mt-3"><summary className="text-xs text-gold-soft cursor-pointer">Scope included ({(p.scope_included || []).length})</summary>
                <div className="mt-2 space-y-2">
                  {Object.entries(cfg.scope_library).map(([grp, items]) => (
                    <div key={grp}><p className={label}>{grp}</p><div className="flex flex-wrap gap-1.5 mt-1">
                      {items.map((it) => <button key={it} onClick={() => toggleArr(i, "scope_included", it)} className={`rounded-full px-2.5 py-1 text-[11px] border ${(p.scope_included || []).includes(it) ? "bg-emerald-500/20 text-emerald-200 border-emerald-500/40" : "border-[color:var(--border-subtle)] text-white/60"}`}>{it}</button>)}
                    </div></div>
                  ))}
                </div>
              </details>
              <details className="mt-2"><summary className="text-xs text-gold-soft cursor-pointer">Exclusions ({(p.exclusions || []).length})</summary>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {cfg.exclusions_library.map((it) => <button key={it} onClick={() => toggleArr(i, "exclusions", it)} className={`rounded-full px-2.5 py-1 text-[11px] border ${(p.exclusions || []).includes(it) ? "bg-red-500/20 text-red-200 border-red-500/40" : "border-[color:var(--border-subtle)] text-white/60"}`}>{it}</button>)}
                </div>
              </details>
            </div>
          ))}
          {q.style === "packages" && q.packages.length < 3 && <button onClick={addPkg} className="w-full inline-flex items-center justify-center gap-1 rounded-lg border border-dashed border-[color:var(--border-subtle)] py-2 text-xs text-white/70 hover:bg-white/5"><Plus className="h-4 w-4" /> Add package</button>}
        </div>
        <Field l="Terms"><textarea rows={2} className={`${inputCls} mt-3`} value={q.terms} onChange={(e) => setQ({ ...q, terms: e.target.value })} /></Field>
      </section>

      {/* Send info */}
      {sendInfo && <QuoteSendInfo sendInfo={sendInfo} snap={snap} />}

      {/* Actions */}
      <div className="sticky bottom-2 z-10 grid grid-cols-2 sm:grid-cols-4 gap-2 rounded-xl border border-[color:var(--border-subtle)] bg-[#101011]/95 backdrop-blur p-2">
        <button onClick={save} disabled={saving || readOnly} className="inline-flex items-center justify-center gap-1 rounded-lg border border-[color:var(--border-subtle)] py-2.5 text-sm text-white hover:bg-white/5 disabled:opacity-50" data-testid="quote-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</button>
        {q.token && <a href={`/quote/${q.token}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1 rounded-lg border border-[color:var(--border-subtle)] py-2.5 text-sm text-white hover:bg-white/5"><Eye className="h-4 w-4" /> Preview</a>}
        {q.status === "Accepted" ? (
          q.job_id ? <span className="inline-flex items-center justify-center gap-1 rounded-lg bg-emerald-500/15 border border-emerald-500/30 py-2.5 text-sm text-emerald-300"><CheckCircle2 className="h-4 w-4" /> Job created</span>
          : <button onClick={convert} className="inline-flex items-center justify-center gap-1 rounded-lg bg-gold py-2.5 text-sm font-semibold text-black" data-testid="quote-convert"><Briefcase className="h-4 w-4" /> Create job</button>
        ) : <button onClick={send} className="inline-flex items-center justify-center gap-1 rounded-lg bg-gold py-2.5 text-sm font-semibold text-black hover:bg-gold-soft" data-testid="quote-send"><Send className="h-4 w-4" /> {q.status === "Draft" ? "Send" : "Resend"}</button>}
        {readOnly ? <button onClick={revise} className="inline-flex items-center justify-center gap-1 rounded-lg border border-[color:var(--border-subtle)] py-2.5 text-sm text-white"><RefreshCw className="h-4 w-4" /> Revise</button>
          : <button onClick={revise} className="inline-flex items-center justify-center gap-1 rounded-lg border border-[color:var(--border-subtle)] py-2.5 text-sm text-white/70 hover:bg-white/5"><RefreshCw className="h-4 w-4" /> New version</button>}
      </div>

      {gateOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setGateOpen(false)} data-testid="quote-confidence-gate">
          <div className="w-full max-w-md rounded-2xl border border-[color:var(--border-subtle)] bg-[#101011] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/15 border border-amber-500/30"><ShieldAlert className="h-5 w-5 text-amber-300" /></div>
              <div>
                <h3 className="font-display text-lg font-semibold text-white">Send a {(conf?.level || "").toLowerCase()}-confidence quote?</h3>
                <p className="text-sm text-muted-c mt-1">This quote passed {conf?.ok_count}/{conf?.total} readiness checks. A few things still look unfinished:</p>
              </div>
            </div>
            <div className="mt-4 space-y-1.5">
              {(conf?.factors || []).filter((f) => !f.ok).map((f) => (
                <div key={f.key} className="flex items-center gap-2 text-sm text-amber-200/90" data-testid={`gate-missing-${f.key}`}>
                  <X className="h-4 w-4 text-red-300 shrink-0" /> {f.label}
                </div>
              ))}
            </div>
            <div className="mt-6 flex gap-2">
              <button onClick={() => setGateOpen(false)} data-testid="gate-review" className="flex-1 rounded-xl border border-[color:var(--border-subtle)] py-2.5 text-sm font-semibold text-white hover:bg-white/5">Review first</button>
              <button onClick={doSend} data-testid="gate-send-anyway" className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl bg-gold py-2.5 text-sm font-semibold text-black hover:bg-gold-soft"><Send className="h-4 w-4" /> Send anyway</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
